import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { adminApi, ApiError, type UserSummary } from '../lib/api.js';
import { summarizeMemberships } from '../lib/groups.js';
import { GroupPicker } from './GroupPicker.js';

export function UserRow({
  user,
  allGroups,
  onChange,
}: {
  user: UserSummary;
  allGroups: { name: string; description?: string }[];
  onChange: () => void;
}) {
  const [editingGroups, setEditingGroups] = useState(false);
  const [pending, setPending] = useState<string[]>([]);
  const [groupError, setGroupError] = useState<string | null>(null);

  function track<T>(group: string, p: Promise<T>): Promise<T> {
    setPending((s) => [...s, group]);
    return p.finally(() => setPending((s) => s.filter((g) => g !== group)));
  }

  const toggleMutation = useMutation({
    mutationFn: (group: string) =>
      track(
        group,
        user.groups.includes(group)
          ? adminApi.removeGroup(user.email, group)
          : adminApi.addGroup(user.email, group),
      ),
    onMutate: () => setGroupError(null),
    onSuccess: onChange,
    onError: (err) => setGroupError(describeError(err)),
  });
  const deleteMutation = useMutation({
    mutationFn: () => adminApi.deleteUser(user.email),
    onSuccess: onChange,
    onError: (err) => alert(`Delete failed: ${describeError(err)}`),
  });
  const resendMutation = useMutation({
    mutationFn: () => adminApi.resendInvite(user.email),
    onError: (err) => alert(`Resend failed: ${describeError(err)}`),
  });

  function handleDelete() {
    if (confirm(`Delete user ${user.email}? This cannot be undone.`)) {
      deleteMutation.mutate();
    }
  }

  const memberships = summarizeMemberships(user.groups);

  return (
    <>
      <tr className={'border-t border-brand-100 ' + (editingGroups ? 'bg-brand-50/60' : '')}>
        <td className="px-3 py-2 align-top">{user.email}</td>
        <td className="px-3 py-2 align-top">{user.name}</td>
        <td className="px-3 py-2 align-top text-xs">
          <StatusBadge status={user.status} enabled={user.enabled} />
        </td>
        <td className="px-3 py-2 align-top">
          <div className="flex flex-wrap gap-1">
            {memberships.length === 0 && <span className="text-brand-400 text-xs">(none)</span>}
            {memberships.map((m) => (
              <span
                key={m.app}
                className="text-xs bg-brand-100 text-brand-800 px-2 py-0.5 whitespace-nowrap"
                title={user.groups.filter((g) => g.startsWith(m.app + ':')).join(', ')}
              >
                <span className="font-medium">{m.label}</span>
                <span className="text-brand-600"> · {m.roles.join(', ')}</span>
              </span>
            ))}
          </div>
        </td>
        <td className="px-3 py-2 align-top">
          <div className="flex gap-2 text-xs">
            <button
              onClick={() => {
                setEditingGroups((v) => !v);
                setGroupError(null);
              }}
              className="text-brand-700 hover:underline"
            >
              {editingGroups ? 'Done' : 'Edit groups'}
            </button>
            {user.status === 'FORCE_CHANGE_PASSWORD' && (
              <button
                onClick={() => resendMutation.mutate()}
                disabled={resendMutation.isPending}
                className="text-brand-700 hover:underline disabled:opacity-50"
              >
                {resendMutation.isPending ? 'Sending…' : 'Resend invite'}
              </button>
            )}
            <button onClick={handleDelete} className="text-red-700 hover:underline">
              Delete
            </button>
          </div>
        </td>
      </tr>
      {editingGroups && (
        <tr className="bg-brand-50/60">
          <td colSpan={5} className="px-3 pb-3 pt-1">
            <div className="border border-brand-200 bg-white p-3">
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-xs text-brand-500">
                  Permissions for <span className="text-brand-800">{user.email}</span> — changes
                  save immediately. The user must sign out and back in to pick them up.
                </span>
                <button
                  onClick={() => setEditingGroups(false)}
                  className="text-xs text-brand-700 hover:underline"
                >
                  Done
                </button>
              </div>
              <GroupPicker
                allGroups={allGroups}
                selected={user.groups}
                pending={pending}
                onToggle={(g) => toggleMutation.mutate(g)}
              />
              {groupError && (
                <div className="mt-2 text-xs text-red-700">Couldn't update group: {groupError}</div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return 'your session expired — reload and sign in again';
    if (err.status === 403) return 'you need the auth:admin group';
    return `${err.status} ${err.message}`.trim();
  }
  return err instanceof Error ? err.message : String(err);
}

function StatusBadge({ status, enabled }: { status: string; enabled: boolean }) {
  if (!enabled) {
    return <span className="bg-red-100 text-red-800 px-2 py-0.5">Disabled</span>;
  }
  if (status === 'CONFIRMED') {
    return <span className="bg-green-100 text-green-800 px-2 py-0.5">Active</span>;
  }
  if (status === 'FORCE_CHANGE_PASSWORD') {
    return <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5">Pending first login</span>;
  }
  return <span className="bg-brand-100 text-brand-800 px-2 py-0.5">{status}</span>;
}
